import { supabase } from "./supabase";
import type {
  DraftProvider,
  NewsroomSettings,
  NewsSource,
  ProviderUsage,
  ResearchResult,
  SourceKind,
  Story,
  StoryBundle,
  StoryStatus,
} from "./types";

export async function bootstrapNewsroom(): Promise<void> {
  const { error } = await supabase.rpc("bootstrap_newsroom");
  if (error) {
    throw error;
  }
}

export async function loadStories(
  status: StoryStatus | "all",
): Promise<Story[]> {
  let query = supabase
    .from("story_inbox")
    .select("*")
    .order("updated_at", { ascending: false })
    .limit(100);
  if (status === "all") {
    query = query.neq("status", "archived");
  } else {
    query = query.eq("status", status);
  }
  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return (data ?? []).map((story) => ({
    ...story,
    confidence: Number(story.confidence),
  })) as Story[];
}

export async function loadStoryBundle(
  clusterId: string,
): Promise<StoryBundle> {
  const [articlesResult, draftsResult] = await Promise.all([
    supabase
      .from("articles")
      .select(
        "id, cluster_id, url, title, source_name, source_domain, author, excerpt, published_at, trust_tier, is_primary_source",
      )
      .eq("cluster_id", clusterId)
      .order("trust_tier", { ascending: true }),
    supabase
      .from("drafts")
      .select("*")
      .eq("cluster_id", clusterId)
      .order("variant", { ascending: true }),
  ]);
  if (articlesResult.error) {
    throw articlesResult.error;
  }
  if (draftsResult.error) {
    throw draftsResult.error;
  }
  return {
    articles: articlesResult.data ?? [],
    drafts: draftsResult.data ?? [],
  } as StoryBundle;
}

export async function saveDraft(draftId: string, body: string): Promise<void> {
  const { error } = await supabase
    .from("drafts")
    .update({ body })
    .eq("id", draftId);
  if (error) {
    throw error;
  }
}

export async function setDraftStatus(
  draftId: string,
  status: "draft" | "approved" | "archived",
): Promise<void> {
  const { error } = await supabase
    .from("drafts")
    .update({ status })
    .eq("id", draftId);
  if (error) {
    throw error;
  }
}

export async function archiveStory(clusterId: string): Promise<void> {
  const { error } = await supabase.rpc("archive_newsroom_story", {
    target_cluster: clusterId,
  });
  if (error) {
    throw error;
  }
}

export async function runResearch(force = true): Promise<ResearchResult> {
  const { data, error } = await supabase.functions.invoke("research", {
    body: {
      force,
      max_sources: 6,
    },
  });
  if (error) {
    throw error;
  }
  return data as ResearchResult;
}

export async function setAutomationEnabled(
  ownerId: string,
  enabled: boolean,
): Promise<void> {
  const { error: rpcError } = await supabase.rpc(
    "set_newsroom_automation",
    {
      is_enabled: enabled,
    },
  );
  if (!rpcError) {
    return;
  }
  if (
    rpcError.code !== "PGRST202" &&
    !rpcError.message.includes("schema cache")
  ) {
    throw new Error(rpcError.message);
  }

  const { data, error } = await supabase
    .from("newsroom_settings")
    .update({ automation_enabled: enabled })
    .eq("owner_id", ownerId)
    .select("owner_id")
    .maybeSingle();
  if (error) {
    if (
      error.code === "PGRST204" ||
      error.message.includes("automation_enabled") ||
      error.message.includes("schema cache")
    ) {
      throw new Error(
        "Automation setup is incomplete. Apply the latest Supabase migrations, then refresh.",
      );
    }
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Newsroom settings are not configured");
  }
}

export async function generateDrafts(
  clusterId: string,
  provider: DraftProvider,
  model?: string,
): Promise<void> {
  const { error } = await supabase.functions.invoke("draft", {
    body: {
      cluster_id: clusterId,
      provider,
      model: model || undefined,
    },
  });
  if (error) {
    throw error;
  }
}

export async function loadSources(): Promise<NewsSource[]> {
  const { data, error } = await supabase
    .from("news_sources")
    .select("*")
    .order("created_at", { ascending: true });
  if (error) {
    throw error;
  }
  return (data ?? []) as NewsSource[];
}

export async function addSource(input: {
  ownerId: string;
  name: string;
  kind: SourceKind;
  endpoint: string | null;
  searchQuery: string | null;
  trustTier: number;
  isPrimary: boolean;
  refreshMinutes: number;
}): Promise<void> {
  const { error } = await supabase.from("news_sources").insert({
    owner_id: input.ownerId,
    name: input.name,
    kind: input.kind,
    endpoint: input.endpoint,
    search_query: input.searchQuery,
    trust_tier: input.trustTier,
    is_primary_source: input.isPrimary,
    refresh_minutes: input.refreshMinutes,
  });
  if (error) {
    throw error;
  }
}

export async function updateSource(
  sourceId: string,
  updates: Partial<
    Pick<
      NewsSource,
      | "enabled"
      | "name"
      | "trust_tier"
      | "is_primary_source"
      | "refresh_minutes"
    >
  >,
): Promise<void> {
  const { error } = await supabase
    .from("news_sources")
    .update(updates)
    .eq("id", sourceId);
  if (error) {
    throw error;
  }
}

export async function deleteSource(sourceId: string): Promise<void> {
  const { error } = await supabase
    .from("news_sources")
    .delete()
    .eq("id", sourceId);
  if (error) {
    throw error;
  }
}

export async function loadSettings(): Promise<NewsroomSettings> {
  const { data, error } = await supabase
    .from("newsroom_settings")
    .select("*")
    .single();
  if (error) {
    throw error;
  }
  return {
    ...data,
    minimum_confidence: Number(data.minimum_confidence),
  } as NewsroomSettings;
}

export async function saveSettings(
  settings: NewsroomSettings,
  previousMinimumConfidence: number,
): Promise<void> {
  const { error } = await supabase
    .from("newsroom_settings")
    .update({
      preferred_provider: settings.preferred_provider,
      openai_model: settings.openai_model,
      openrouter_model: settings.openrouter_model,
      minimum_confidence: settings.minimum_confidence,
      exa_daily_limit: settings.exa_daily_limit,
      firecrawl_daily_limit: settings.firecrawl_daily_limit,
      model_daily_limit: settings.model_daily_limit,
      retention_days: settings.retention_days,
    })
    .eq("owner_id", settings.owner_id);
  if (error) {
    throw error;
  }
  if (settings.minimum_confidence !== previousMinimumConfidence) {
    const { error: recomputeError } = await supabase.functions.invoke(
      "research",
      {
        body: { recompute_only: true },
      },
    );
    if (recomputeError) {
      throw recomputeError;
    }
  }
}

export async function loadUsage(): Promise<ProviderUsage[]> {
  const since = new Date();
  since.setUTCHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from("provider_usage")
    .select("provider, operation, units, created_at")
    .gte("created_at", since.toISOString())
    .order("created_at", { ascending: false });
  if (error) {
    throw error;
  }
  return (data ?? []) as ProviderUsage[];
}
